import React from 'react';
import { Plus, X, Plane } from 'lucide-react';
import { City } from '../types';
import { Airport, searchAirports } from '../services/airports';

interface CityInputProps {
  selectedCities: City[];
  onAddCity: (city: string) => void;
  onRemoveCity: (cityId: string) => void;
  placeholder: string;
  maxCities?: number;
  isSingleSelect?: boolean;
}

export function CityInput({ 
  selectedCities, 
  onAddCity, 
  onRemoveCity, 
  placeholder, 
  maxCities,
  isSingleSelect 
}: CityInputProps) {
  const [query, setQuery] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<Airport[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);

  const searchTimeout = React.useRef<NodeJS.Timeout>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const suggestionsRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = React.useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchAirports(searchQuery);
      setSuggestions(results);
    } catch (err) {
      console.error('Error searching airports:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleSelectAirport = (airport: Airport) => {
    if (isSingleSelect) {
      // For origin city, replace current selection
      selectedCities.forEach(city => onRemoveCity(city.id));
      onAddCity(airport.city);
    } else {
      // For destination cities, add to list if not already selected
      const isAlreadySelected = selectedCities.some(city => city.name === airport.city);
      if (!isAlreadySelected) {
        onAddCity(airport.city);
      }
    }
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Prevent clearing input when clicking inside it
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSuggestions(true);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="relative">
          <Plane className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onFocus={() => setShowSuggestions(true)}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {query.length >= 3 && showSuggestions && (
          <div
            ref={suggestionsRef}
            className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto"
          >
            {isLoading ? (
              <div className="p-3 text-sm text-gray-500">Buscando...</div>
            ) : suggestions.length > 0 ? (
              suggestions.map((airport) => {
                const isSelected = selectedCities.some(city => city.name === airport.city);
                return (
                  <button
                    key={airport.id}
                    type="button"
                    onClick={() => handleSelectAirport(airport)}
                    disabled={isSelected}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                      isSelected ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {airport.city}
                    </span>
                    {isSelected && (
                      <span className="ml-2 text-xs text-gray-500">
                        (já selecionada)
                      </span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="p-3 text-sm text-gray-500">
                Nenhuma cidade encontrada
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        {selectedCities.map((city) => (
          <span
            key={city.id}
            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full"
          >
            {city.name}
            <button
              type="button"
              onClick={() => onRemoveCity(city.id)}
              className="p-0.5 hover:bg-blue-200 rounded-full transition-colors"
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}